import { Box } from '@chakra-ui/react';
import { BaseDialog, BaseTable, FilterForm, useHookForm } from '@invara-dev/react-base';
import { UseDisclosureReturn } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useState } from 'react';
import { formatDateDayJs } from '../../../common/formatters';
import { useMyReportQuery } from './api/useMyReportQuery';
import { AgentActivityReportDetailRow, MyReportFilters } from './types';

type MyReportDialogProps = {
  disclosure: UseDisclosureReturn;
};

const columnHelper = createColumnHelper<AgentActivityReportDetailRow>();

const columns = [
  columnHelper.accessor('FlsqNumber', { header: 'FLSQ Number' }),
  columnHelper.accessor('TimeStamp', {
    header: 'Date',
    cell: (info) => (info.getValue() ? new Date(info.getValue()).toLocaleString() : '')
  }),
  columnHelper.accessor('FareAmount', {
    header: 'Fare',
    cell: (info) => `$${Number(info.getValue()).toFixed(2)}`
  }),
  columnHelper.accessor('CommissionOrServiceFeeAmount', {
    header: 'Commission / Service Fee',
    cell: (info) => `$${Number(info.getValue()).toFixed(2)}`
  })
];

function buildTodayFilters(): MyReportFilters {
  const t = formatDateDayJs(dayjs());
  return {
    StartDate: t,
    EndDate: t,
    DateDisplay: `${t} - ${t}`,
    FlsqNumber: ''
  };
}

export function MyReportDialog({ disclosure }: MyReportDialogProps) {
  const [filterState, setFilterState] = useState<MyReportFilters>(buildTodayFilters);

  const { formMethods, fieldHelper } = useHookForm<MyReportFilters>(filterState);

  const query = useMyReportQuery(filterState.StartDate, filterState.EndDate, filterState.FlsqNumber);

  const fields = [
    fieldHelper
      .dateRangeInput('DateDisplay', { startDate: 'StartDate', endDate: 'EndDate', maxDate: new Date() })
      .withLabel('Date Range')
      .withValidation({ required: {} }),
    fieldHelper
      .textInput('FlsqNumber')
      .withLabel('FLSQ number')
      .isNumeric()
      .withValidation({ pattern: { value: /^\d*$/, message: 'FLSQ number must be numeric' } })
  ];

  return (
    <BaseDialog
      disclosure={disclosure}
      header='My Report'
      dragDialog={{ initialW: 1100, initialH: 700, minW: 760, minH: 480 }}>
      <Box display='flex' flexDirection='column' h='100%' minH={0} py={2}>
        <FilterForm
          formFields={fields}
          formMethods={formMethods}
          onSubmit={async (val) => {
            setFilterState(val);
            return Promise.resolve();
          }}
        />
        <Box flex='1 1 auto' minH={0} overflow='hidden'>
          <BaseTable
            data={query.data ?? []}
            isLoading={query.isLoading}
            columns={columns}
            disableGlobalFilter
            tableHeight='100%'
          />
        </Box>
      </Box>
    </BaseDialog>
  );
}
